import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, ActionIcon, Tooltip, Text, Paper, Group } from "@mantine/core";
import { DataTable, ConfirmModal, Button, Alert, Badge } from "@xplatform/design-system";
import type { DataTableColumn } from "@xplatform/design-system";
import {
    IconSettings,
    IconPlus,
    IconEdit,
    IconTrash,
} from "@tabler/icons-react";
import { useDoctype } from "@hooks/flow";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { useTranslation } from "@hooks/useTranslation";
import { CreateDoctypeDialog, EditDoctypeDialog } from "@src/features";
import type { DocumentTypeDetailViewModel } from "@models/doctype";
import { getLastPartOfUid } from "@src/utils/uidUtil";
import "./DoctypeManagementPage.scss";

export const DoctypeManagementPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { canEditDoctype, canCreateDoctype, isSuperAdmin } = useAdminPermissions();
    const {
        doctypesDetail,
        isLoading,
        error,
        fetchDoctypesDetailByProject,
        deleteDoctype,
    } = useDoctype();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingDoctype, setEditingDoctype] =
        useState<DocumentTypeDetailViewModel | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deletingDoctype, setDeletingDoctype] =
        useState<DocumentTypeDetailViewModel | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    useEffect(() => {
        fetchDoctypesDetailByProject();
    }, [fetchDoctypesDetailByProject]);

    const handleViewFlows = (docTypeId: string, docTypeCode: string) => {
        navigate(`/admin/doctypes/${docTypeId}/flows`, {
            state: { docTypeCode },
        });
    };

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleDoctypeCreated = async () => {
        await fetchDoctypesDetailByProject();
    };

    const handleEditDoctype = (doctype: DocumentTypeDetailViewModel) => {
        setEditingDoctype(doctype);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingDoctype(null);
    };

    const handleDoctypeUpdated = async () => {
        await fetchDoctypesDetailByProject();
    };

    const handleDeleteClick = (doctype: DocumentTypeDetailViewModel) => {
        setDeletingDoctype(doctype);
        setDeleteError(null);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setDeletingDoctype(null);
        setDeleteError(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingDoctype) return;
        try {
            await deleteDoctype(deletingDoctype.id);
            setDeleteConfirmOpen(false);
            setDeletingDoctype(null);
            setDeleteError(null);
        } catch (err) {
            setDeleteError(
                err instanceof Error ? err.message : "Failed to delete document type",
            );
        }
    };

    const columns: DataTableColumn<DocumentTypeDetailViewModel>[] = [
        {
            key: "id",
            header: "ID",
            width: "8%",
            render: (doctype) => (
                <span className="doctype-page__cell-id">
                    {getLastPartOfUid(doctype.id)}
                </span>
            ),
        },
        {
            key: "name",
            header: t("admin.table.columns.name"),
            width: "16%",
            render: (doctype) => (
                <Text size="sm" fw={500}>
                    {doctype.docTypeName}
                </Text>
            ),
        },
        {
            key: "description",
            header: t("admin.table.columns.description"),
            width: "24%",
            render: (doctype) => (
                <Text
                    size="sm"
                    c="dimmed"
                    className="doctype-page__cell-description"
                >
                    {doctype.description || "-"}
                </Text>
            ),
        },
        {
            key: "category",
            header: t("admin.categories.title"),
            width: "12%",
            render: (doctype) => (
                <Text size="sm" c="dimmed">
                    {doctype.categoryName || "-"}
                </Text>
            ),
        },
        {
            key: "organization",
            header: t("admin.table.columns.organization"),
            width: "14%",
            render: (doctype) => (
                <Text size="sm" c="dimmed">
                    {doctype.organizationId || "-"}
                </Text>
            ),
        },
        {
            key: "status",
            header: t("admin.table.columns.status"),
            width: "8%",
            render: (doctype) => (
                <Badge
                    size="sm"
                    color={
                        doctype.status.toLowerCase() === "active" ? "green" : "gray"
                    }
                >
                    {t(`admin.table.status.${doctype.status.toLowerCase()}`)}
                </Badge>
            ),
        },
        {
            key: "updated",
            header: t("admin.table.columns.updated"),
            width: "8%",
            render: (doctype) => (
                <Text size="sm" c="dimmed">
                    {doctype.updatedAt?.toLocaleDateString() || "-"}
                </Text>
            ),
        },
    ];

    if (canEditDoctype) {
        columns.push({
            key: "actions",
            header: t("admin.table.columns.actions"),
            width: "10%",
            align: "right",
            render: (doctype) => (
                <Group gap={4} justify="flex-end">
                    <Tooltip label={t("common.edit")}>
                        <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            onClick={() => handleEditDoctype(doctype)}
                        >
                            <IconEdit size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("admin.table.tooltips.viewFlowConfigs")}>
                        <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            aria-label={t("admin.table.tooltips.viewFlowConfigs")}
                            onClick={() => handleViewFlows(doctype.id, doctype.docTypeCode)}
                        >
                            <IconSettings size={16} />
                        </ActionIcon>
                    </Tooltip>
                    {isSuperAdmin && (
                        <Tooltip label={t("common.delete")}>
                            <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                aria-label={t("common.delete")}
                                onClick={() => handleDeleteClick(doctype)}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
            ),
        });
    }

    return (
        <div className="admin-page-content">
            <Group
                justify="space-between"
                align="center"
                mb="lg"
                className="project-doctype-page__header"
            >
                <Text component="h1" size="xl" fw={700}>
                    {t("admin.documentTypes.title")}
                </Text>
                {canCreateDoctype && (
                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={handleOpenDialog}
                    >
                        {t("admin.documentTypes.actions.createDocType")}
                    </Button>
                )}
            </Group>

            {error && (
                <Alert color="red" mb="sm">
                    {error}
                </Alert>
            )}

            {isLoading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "2rem 0",
                    }}
                >
                    <Loader />
                </div>
            ) : doctypesDetail.length === 0 ? (
                <div className="doctype-page__empty-state">
                    <IconSettings size={48} color="gray" />
                    <Text size="lg" c="dimmed" mb="xs">
                        {t("admin.documentTypes.empty.noDocTypes")}
                    </Text>
                    <Text size="sm" c="dimmed">
                        {t("admin.documentTypes.empty.description")}
                    </Text>
                </div>
            ) : (
                <Paper className="doctype-page__table-container">
                    <DataTable<DocumentTypeDetailViewModel>
                        className="doctype-page__table"
                        fz="sm"
                        verticalSpacing="sm"
                        horizontalSpacing="md"
                        columns={columns}
                        data={doctypesDetail}
                        getRowKey={(doctype) => doctype.id}
                    />
                </Paper>
            )}

            <CreateDoctypeDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSuccess={handleDoctypeCreated}
            />

            {editingDoctype && (
                <EditDoctypeDialog
                    open={editDialogOpen}
                    onClose={handleCloseEditDialog}
                    onSuccess={handleDoctypeUpdated}
                    doctype={editingDoctype}
                />
            )}

            <ConfirmModal
                opened={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={`${t("common.delete")} ${t("admin.documentTypes.title")}`}
                confirmLabel={t("common.delete")}
                cancelLabel={t("common.cancel")}
                danger
                loading={isLoading}
                portalProps={{ target: "#admin-portal-root" }}
                message={
                    <>
                        {deleteError && (
                            <Alert color="red" mb="sm">
                                {deleteError}
                            </Alert>
                        )}
                        <Text size="sm">
                            Are you sure you want to delete{" "}
                            <strong>{deletingDoctype?.docTypeName}</strong> (
                            {deletingDoctype?.docTypeCode})? This will also delete all
                            associated flow configurations. This action cannot be undone.
                        </Text>
                    </>
                }
            />
        </div>
    );
};
